/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  resolveAdereMonthsForUi,
  resolveDefaultAdereMonth,
} from './constants';

const formatDataEstimada = (epoch, pattern = 'DD/MM/YYYY') => {
  if (epoch == null) return null;
  const parsed = moment(Number(epoch) > 0 ? Number(epoch) : epoch);
  return parsed.isValid() ? parsed.format(pattern) : null;
};

/** Mês Adere calendário atual (MM/YYYY). */
const isAdereMonthCorrente = (adereMonth) =>
  adereMonth != null && adereMonth === moment().format('MM/YYYY');

/** Adere MM/YYYY já encerrado (antes do mês calendário atual). */
const isAdereMonthPassado = (adereMonth) => {
  if (!adereMonth) return false;
  const [mm, yyyy] = adereMonth.split('/').map(Number);
  if (!mm || !yyyy) return false;
  const mes = moment({ year: yyyy, month: mm - 1 }).startOf('month');
  return mes.isBefore(moment().startOf('month'));
};

/**
 * Fallback local (sem feriados) quando a API manda 0/null no mês aberto.
 * Não substitui o cálculo oficial do servidor — só evita UI absurda.
 */
const estimateDiasUteisRestantesNoMes = (adereMonth) => {
  if (!adereMonth || isAdereMonthPassado(adereMonth)) return null;
  const [mm, yyyy] = adereMonth.split('/').map(Number);
  if (!mm || !yyyy) return null;
  const end = moment({ year: yyyy, month: mm - 1 }).endOf('month').startOf('day');
  let cursor = moment().startOf('day');
  if (cursor.isAfter(end, 'day')) return 0;
  let count = 0;
  while (cursor.isSameOrBefore(end, 'day')) {
    const dow = cursor.day();
    if (dow !== 0 && dow !== 6) count += 1;
    cursor = cursor.clone().add(1, 'day');
  }
  return count;
};

const buildProjectionMessage = (sel, adereMonth) => {
  if (!sel) return 'Selecione um mês Adere para ver o progresso.';
  const pendentes = sel.servicosPendentes ?? 0;
  const mesPassado = isAdereMonthPassado(adereMonth);

  if (pendentes === 0) {
    if (mesPassado) {
      return `Adere ${adereMonth} fechado — nada pendente`;
    }
    if (isAdereMonthCorrente(adereMonth)) {
      return `Adere ${adereMonth} sem pendências nas NSIRs atribuídas`;
    }
    return `Adere ${adereMonth} — sem pendências atribuídas`;
  }

  if (sel.fechaNoPrazo === true) {
    const fim = moment(sel.janelaFim).format('DD/MM');
    return `No ritmo — prazo ${fim}/${adereMonth?.split('/')[1] ?? ''}`;
  }

  if (sel.fechaNoPrazo === false && sel.ritmoNecessario != null) {
    return `Atenção — no ritmo atual estoura o mês; precisa ${sel.ritmoNecessario}/dia`;
  }

  return `Pendências: ${pendentes} — digite para ver projeção`;
};

/**
 * Fora da janela: no Adere corrente não alertar (adiantou trabalho).
 * Em meses anteriores, alertar se count > 0 (digitação em meses posteriores).
 */
const shouldShowDigitadosForaJanela = (adereMonth, count) => {
  if (!count || count <= 0) return false;
  if (isAdereMonthCorrente(adereMonth)) return false;
  return true;
};

const resolveDiasUteisRestantesLabel = (sel, adereMonth) => {
  if (isAdereMonthPassado(adereMonth)) return null;
  const fromApi = sel?.diasUteisRestantesAdere;
  if (fromApi != null && fromApi > 0) {
    return `${fromApi} dias úteis restantes`;
  }
  const estimated = estimateDiasUteisRestantesNoMes(adereMonth);
  if (estimated != null && estimated > 0) {
    return `${estimated} dias úteis restantes (estimativa)`;
  }
  return null;
};

/** Dívida primeiro: o que ainda falta neste Adere. */
const buildDebtLine = (sel) => {
  const pendentes = sel.servicosPendentes ?? 0;
  const abertas = sel.nsirsNaFila ?? 0;
  const emInspecao = sel.nsirsEmInspecao ?? 0;

  let line;
  if (pendentes > 0) {
    line = `Faltam ${pendentes} serviço${pendentes === 1 ? '' : 's'} · ${abertas} NSIR${abertas === 1 ? '' : 's'} em aberto`;
  } else if (abertas > 0) {
    line = `Nada pendente · ${abertas} NSIR${abertas === 1 ? '' : 's'} em aberto`;
  } else {
    line = 'Nada pendente neste Adere';
  }

  if (emInspecao > 0) {
    line += ` · ${emInspecao} em inspeção`;
  }
  return line;
};

/** Contexto: já feito + NSIRs fechadas do total do Adere. */
const buildContextLine = (sel) => {
  const digitados = sel.servicosDigitados ?? 0;
  const fechadas = sel.nsirsFechadas ?? 0;
  const abertas = sel.nsirsNaFila ?? 0;
  const emInspecao = sel.nsirsEmInspecao ?? 0;
  const totalNsirs = abertas + fechadas + emInspecao;

  if (totalNsirs > 0) {
    return `${digitados} já feitos · ${fechadas} NSIR${fechadas === 1 ? '' : 's'} fechada${fechadas === 1 ? '' : 's'} de ${totalNsirs}`;
  }
  if (digitados > 0) {
    return `${digitados} já feitos`;
  }
  return null;
};

const buildProjecaoTooltip = (sel) => {
  const digitados = sel.servicosDigitados ?? 0;
  const decorridos = sel.diasUteisDecorridosAdere;
  const ritmo = sel.ritmoAdere;
  const pendentes = sel.servicosPendentes ?? 0;
  const dias = sel.diasNecessarios;
  const parts = [];
  if (decorridos != null && digitados > 0) {
    const ritmoLabel = ritmo != null ? ` (~${Math.round(ritmo)}/dia)` : '';
    parts.push(`${digitados} digitados em ${decorridos} dias úteis${ritmoLabel}`);
  }
  if (pendentes > 0 && dias != null) {
    parts.push(`${pendentes} pendentes → ~${dias} dia${dias === 1 ? '' : 's'} útil${dias === 1 ? '' : 'eis'}`);
  }
  return parts.length
    ? `Projeção no ritmo atual: ${parts.join('; ')}.`
    : 'Data em que a dívida zera no ritmo atual (não é o prazo do Adere).';
};

const AderePeriodoCard = ({
  adere,
  inicio,
  fim,
  selectedMonth,
  onSelectMonth,
  loading,
  refreshing,
}) => {
  const meses = useMemo(
    () => resolveAdereMonthsForUi(adere, inicio, fim),
    [adere, inicio, fim],
  );
  const activeMonth = selectedMonth
    ?? resolveDefaultAdereMonth(adere, meses);
  const sel = !refreshing && adere?.selecionado?.adereMonth === activeMonth
    ? adere.selecionado
    : (!refreshing ? adere?.selecionado : null);

  const dataProjecao = formatDataEstimada(sel?.dataEstimadaFim, 'DD/MM');
  const chipsDisabled = loading || refreshing;
  const showForaJanela = shouldShowDigitadosForaJanela(
    activeMonth,
    sel?.digitadosForaJanela ?? 0,
  );
  const showBodyLoading = loading || refreshing;
  const emptyMonths = !meses.length && !loading;
  const diasUteisLabel = sel ? resolveDiasUteisRestantesLabel(sel, activeMonth) : null;
  const debtLine = sel ? buildDebtLine(sel) : null;
  const contextLine = sel ? buildContextLine(sel) : null;
  const showProjecao = Boolean(dataProjecao && (sel?.servicosPendentes ?? 0) > 0);
  const tempoLine = [diasUteisLabel, showProjecao ? `projeção ${dataProjecao}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Adere do mês
      </Typography>

      <Stack spacing={1.25} sx={{ flex: 1 }}>
        {emptyMonths ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum mês Adere cruzando o ciclo atual.
          </Typography>
        ) : (
          <>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
              {meses.map((mes) => (
                <Chip
                  key={mes}
                  label={mes}
                  size="small"
                  variant={mes === activeMonth ? 'filled' : 'outlined'}
                  color={mes === activeMonth ? 'primary' : 'default'}
                  onClick={() => onSelectMonth(mes)}
                  disabled={chipsDisabled}
                  sx={{ fontWeight: mes === activeMonth ? 700 : 400 }}
                />
              ))}
            </Stack>

            {showBodyLoading ? (
              <Stack spacing={1} sx={{ py: 1 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary">
                  Carregando Adere…
                </Typography>
              </Stack>
            ) : (
              <>
                <Typography variant="body2" fontWeight={500}>
                  {buildProjectionMessage(sel, activeMonth)}
                </Typography>

                {sel && (
                  <Stack spacing={0.35}>
                    <Typography variant="caption" color="text.primary" fontWeight={500}>
                      {debtLine}
                    </Typography>
                    {contextLine && (
                      <Typography variant="caption" color="text.secondary">
                        {contextLine}
                      </Typography>
                    )}
                    {tempoLine && (
                      showProjecao ? (
                        <Tooltip title={buildProjecaoTooltip(sel)}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ cursor: 'help', width: 'fit-content' }}
                          >
                            {tempoLine}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {tempoLine}
                        </Typography>
                      )
                    )}
                    {showForaJanela && (
                      <Alert severity="warning" sx={{ py: 0, mt: 0.5 }}>
                        {sel.digitadosForaJanela} digitados em meses posteriores
                      </Alert>
                    )}
                  </Stack>
                )}

                <Tooltip title="Conta só NSIRs com este Adere atribuídas a você. Meus serviços pode incluir outro mês Adere ou sem Adere.">
                  <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help', mt: 'auto' }}>
                    *** Só NSIRs com este Adere
                  </Typography>
                </Tooltip>
              </>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};

AderePeriodoCard.propTypes = {
  adere: PropTypes.shape({
    mesesDisponiveis: PropTypes.arrayOf(PropTypes.string),
    defaultAdereMonth: PropTypes.string,
    selecionado: PropTypes.object,
  }),
  inicio: PropTypes.number,
  fim: PropTypes.number,
  selectedMonth: PropTypes.string,
  onSelectMonth: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
};

AderePeriodoCard.defaultProps = {
  adere: null,
  inicio: null,
  fim: null,
  selectedMonth: null,
  loading: false,
  refreshing: false,
};

export default AderePeriodoCard;
